import { Alert } from 'antd'
import { readInspectionAreas } from 'features/libra/inspectionPlanning/planningModel'
import {
  planningAreasConfigSchema,
  planningSourceCaptureSchema,
} from 'features/libra/inspectionPlanning/planningTypes'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { useStepValidation } from 'features/libra/StepValidationContext'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import {
  LibraWorflowStepComponentProps,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useEffect, useMemo } from 'react'

import { InspectionAreas } from './InspectionAreas'

export const useValidateConfig: UseValidateConfigHook = ({ config }) =>
  useMemo(() => planningAreasConfigSchema.safeParse(config).success, [config])

export const Component = ({
  config: rawConfig,
  readonly,
}: LibraWorflowStepComponentProps<Record<string, unknown>>) => {
  const { globalContext } = useWorkflow()
  const { fieldValues } = useTicketAccess()
  const planningCapture = planningSourceCaptureSchema.safeParse(
    globalContext.planningSourceCapture
  )
  const sourceCaptureIdentity = JSON.stringify(
    planningCapture.success
      ? (unwrapFieldValue(fieldValues[planningCapture.data.sourceField]) ??
          null)
      : null
  )
  const { setValidationResult } = useStepValidation()
  const planning = useMemo(
    () => readInspectionAreas(rawConfig, fieldValues, globalContext),
    [rawConfig, fieldValues, globalContext]
  )
  useEffect(() => {
    if (!planning.success)
      setValidationResult({
        status: 'Error',
        message: 'Inspection area planning needs attention',
      })
  }, [planning.success, setValidationResult])
  return planning.success ? (
    <InspectionAreas
      planning={planning.data}
      sourceCaptureIdentity={sourceCaptureIdentity}
      readonly={readonly}
    />
  ) : (
    <Alert
      type='warning'
      showIcon
      title='Inspection area planning needs attention'
      description='The source zones, saved areas or planning configuration could not be read. Correct them before editing or completing this step.'
    />
  )
}
